import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  History,
  AlertTriangle,
  Info,
} from "lucide-react";
import {
  previewImport,
  executeImport,
  getImportHistory,
  downloadTemplate,
} from "@/services/import_tool";
import type { ImportModule, ImportResult, ImportHistoryItem } from "@/services/types/import_tool";

const MODULE_OPTIONS: { value: ImportModule; label: string; desc: string }[] = [
  // T-S2-A-1
  { value: "hotels",           label: "酒店",   desc: "批量导入酒店基本信息" },
  { value: "vehicles",         label: "车辆",   desc: "批量导入车辆信息" },
  { value: "drivers",          label: "司导",   desc: "批量导入司导信息" },
  // T-S2-A-2
  { value: "attractions",      label: "门票",   desc: "批量导入景区/门票信息" },
  { value: "suppliers",        label: "供应商", desc: "批量导入供应商（4 类型）" },
  { value: "channel_partners", label: "同业B2B",desc: "批量导入 B2B 同业客户" },
  { value: "products",         label: "产品",   desc: "批量导入旅游产品（含排期）" },
  { value: "orders",           label: "订单",   desc: "批量导入历史草稿订单" },
];

function StatusBadge({ value }: { value: boolean }) {
  return value ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      <AlertTriangle size={11} /> 干运行
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
      <CheckCircle2 size={11} /> 已导入
    </span>
  );
}

function ResultCard({ result }: { result: ImportResult }) {
  const hasErrors = result.errors.length > 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        {result.failed === 0 ? (
          <CheckCircle2 size={18} className="text-emerald-600" />
        ) : (
          <AlertTriangle size={18} className="text-amber-500" />
        )}
        <h3 className="text-base font-semibold text-slate-800">
          {result.dry_run ? "预览结果" : "导入结果"}
        </h3>
        <StatusBadge value={result.dry_run} />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg bg-slate-50 p-3 text-center">
          <div className="text-2xl font-bold text-slate-800">{result.total_rows}</div>
          <div className="text-xs text-slate-500 mt-0.5">总行数</div>
        </div>
        <div className="rounded-lg bg-emerald-50 p-3 text-center">
          <div className="text-2xl font-bold text-emerald-700">{result.succeeded}</div>
          <div className="text-xs text-emerald-600 mt-0.5">成功</div>
        </div>
        <div className={`rounded-lg p-3 text-center ${result.failed > 0 ? "bg-red-50" : "bg-slate-50"}`}>
          <div className={`text-2xl font-bold ${result.failed > 0 ? "text-red-700" : "text-slate-400"}`}>
            {result.failed}
          </div>
          <div className={`text-xs mt-0.5 ${result.failed > 0 ? "text-red-600" : "text-slate-400"}`}>失败</div>
        </div>
      </div>

      {hasErrors && (
        <div className="rounded-lg border border-red-200 bg-red-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-red-200 flex items-center gap-2">
            <XCircle size={14} className="text-red-600" />
            <span className="text-sm font-medium text-red-700">错误明细</span>
          </div>
          <div className="divide-y divide-red-100 max-h-52 overflow-y-auto">
            {result.errors.map((e, idx) => (
              <div key={idx} className="px-3 py-2 text-sm">
                <span className="font-mono text-red-500 mr-2">行 {e.row_num}</span>
                <span className="text-slate-600 font-medium mr-1">[{e.column}]</span>
                <span className="text-slate-700">{e.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryTable({ items }: { items: ImportHistoryItem[] }) {
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
        暂无导入记录（重启后清空）
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-slate-600">时间</th>
            <th className="px-4 py-3 text-left font-medium text-slate-600">模块</th>
            <th className="px-4 py-3 text-left font-medium text-slate-600">文件名</th>
            <th className="px-4 py-3 text-right font-medium text-slate-600">总行</th>
            <th className="px-4 py-3 text-right font-medium text-slate-600">成功</th>
            <th className="px-4 py-3 text-right font-medium text-slate-600">失败</th>
            <th className="px-4 py-3 text-left font-medium text-slate-600">类型</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50">
              <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">
                {new Date(item.executed_at).toLocaleString("zh-CN")}
              </td>
              <td className="px-4 py-2.5">
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  {MODULE_OPTIONS.find(m => m.value === item.module)?.label ?? item.module}
                </span>
              </td>
              <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{item.filename}</td>
              <td className="px-4 py-2.5 text-right text-slate-600">{item.total_rows}</td>
              <td className="px-4 py-2.5 text-right text-emerald-700 font-medium">{item.succeeded}</td>
              <td className={`px-4 py-2.5 text-right font-medium ${item.failed > 0 ? "text-red-600" : "text-slate-400"}`}>
                {item.failed}
              </td>
              <td className="px-4 py-2.5">
                <StatusBadge value={item.dry_run} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * /admin/system/import · T-S2-A-1
 * 数据批量导入工具 · owner + tech_admin only
 */
export function ImportToolPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedModule, setSelectedModule] = useState<ImportModule>("hotels");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [activeTab, setActiveTab] = useState<"import" | "history">("import");

  const historyQuery = useQuery({
    queryKey: ["import-history"],
    queryFn: getImportHistory,
    enabled: activeTab === "history",
    refetchOnWindowFocus: false,
  });

  const previewMut = useMutation({
    mutationFn: () => previewImport(selectedModule, selectedFile!),
    onSuccess: setResult,
  });

  const executeMut = useMutation({
    mutationFn: () => executeImport(selectedModule, selectedFile!, dryRun),
    onSuccess: (r) => {
      setResult(r);
      if (!dryRun) {
        historyQuery.refetch();
      }
    },
  });

  const isPending = previewMut.isPending || executeMut.isPending;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setSelectedFile(f);
    setResult(null);
  }

  function handlePreview() {
    if (!selectedFile) return;
    setResult(null);
    previewMut.mutate();
  }

  function handleExecute() {
    if (!selectedFile) return;
    setResult(null);
    executeMut.mutate();
  }

  const mutError = previewMut.error ?? executeMut.error;

  return (
    <div className="flex flex-col gap-5 p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/" className="hover:text-slate-700">首页</Link>
        <span>/</span>
        <span className="text-slate-800 font-medium">数据批量导入</span>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-blue-100 p-2">
          <FileSpreadsheet size={20} className="text-blue-600" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">数据批量导入</h1>
          <p className="text-sm text-slate-500 mt-0.5">将 Excel 数据一次性导入系统 · 支持酒店 / 车辆 / 司导 / 门票 / 供应商 / 同业B2B / 产品 / 订单</p>
        </div>
        <span className="ml-auto text-xs text-slate-400 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
          owner + tech_admin
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {(["import", "history"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab === "import" ? (
              <span className="flex items-center gap-1.5"><Upload size={14} /> 导入</span>
            ) : (
              <span className="flex items-center gap-1.5"><History size={14} /> 历史记录</span>
            )}
          </button>
        ))}
      </div>

      {/* Import tab */}
      {activeTab === "import" && (
        <div className="flex flex-col gap-5">
          {/* Config card */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">导入配置</h2>

            {/* Module selector */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-600 block mb-2">选择模块</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {MODULE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSelectedModule(opt.value); setSelectedFile(null); setResult(null); }}
                    className={`rounded-lg border px-3 py-2.5 text-left transition-all ${
                      selectedModule === opt.value
                        ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-sm font-medium text-slate-800">{opt.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Download template */}
            <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 flex items-center gap-3">
              <Info size={14} className="text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-600 flex-1">下载模板文件，按格式填写后上传</span>
              <button
                onClick={() => downloadTemplate(selectedModule)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white transition-colors"
              >
                <Download size={13} /> 下载模板
              </button>
            </div>

            {/* File upload */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-600 block mb-2">
                上传 Excel 文件 <span className="text-red-500">*</span>
              </label>
              <div
                className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
                  selectedFile ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-blue-300 hover:bg-blue-50/30"
                }`}
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileSpreadsheet size={20} className="text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700">{selectedFile.name}</span>
                    <span className="text-xs text-slate-400">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload size={24} className="text-slate-300" />
                    <p className="text-sm text-slate-500">点击或拖拽上传 <span className="font-medium text-blue-600">.xlsx</span> 文件</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {/* Dry run toggle */}
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  dryRun ? "bg-amber-400" : "bg-emerald-500"
                }`}
                onClick={() => setDryRun((v) => !v)}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                    dryRun ? "translate-x-1" : "translate-x-[18px]"
                  }`}
                />
              </div>
              <span className="text-sm text-slate-700">
                {dryRun ? (
                  <><strong className="text-amber-700">干运行模式</strong> — 只验证，不写入数据库</>
                ) : (
                  <><strong className="text-emerald-700">实际导入模式</strong> — 验证通过后写入数据库</>
                )}
              </span>
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              disabled={!selectedFile || isPending}
              onClick={handlePreview}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <CheckCircle2 size={15} />
              {previewMut.isPending ? "验证中…" : "预览验证"}
            </button>
            <button
              disabled={!selectedFile || isPending}
              onClick={handleExecute}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors ${
                dryRun ? "bg-amber-500 hover:bg-amber-600" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              <Upload size={15} />
              {executeMut.isPending
                ? "处理中…"
                : dryRun
                ? "执行干运行"
                : "确认导入"}
            </button>
          </div>

          {/* Error banner */}
          {mutError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              请求失败：{mutError instanceof Error ? mutError.message : "未知错误"}
            </div>
          )}

          {/* Result card */}
          {result && <ResultCard result={result} />}
        </div>
      )}

      {/* History tab */}
      {activeTab === "history" && (
        <div>
          {historyQuery.isLoading ? (
            <div className="text-center py-8 text-sm text-slate-400">加载中…</div>
          ) : historyQuery.isError ? (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              加载失败
            </div>
          ) : (
            <HistoryTable items={historyQuery.data ?? []} />
          )}
        </div>
      )}
    </div>
  );
}
